import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

(function () {
  function money(cents, format) {
    // money_format can carry markup; this is written into textContent. The
    // theme's helper resolves every amount placeholder, so a market whose
    // format is amount_with_comma_separator keeps its own separators instead
    // of falling back to the US ones.
    var template = String(format || '${{amount}}').replace(/<[^>]*>/g, '').trim();
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    return formatMoney(Number(cents) || 0, template, currency);
  }

  function getSelectedOptions(root) {
    var selects = root.querySelectorAll('[data-option-position]');
    var options = [];
    selects.forEach(function (select) {
      options.push(select.value);
    });
    return options;
  }

  function findVariant(product, selectedOptions) {
    if (!product || !product.variants) return null;
    return (
      product.variants.find(function (variant) {
        return selectedOptions.every(function (value, index) {
          return variant['option' + (index + 1)] === value;
        });
      }) || null
    );
  }

  function updateBuyBox(root, product, variant, isInitial) {
    var variantInput = root.querySelector('[data-variant-id]');
    var priceEl = root.querySelector('[data-price]');
    var compareEl = root.querySelector('[data-compare-price]');
    var atc = root.querySelector('[data-atc]');
    var atcText = root.querySelector('[data-atc-text]');

    // No product JSON means we cannot resolve variants client side. Leave the
    // form exactly as Liquid rendered it rather than locking add-to-cart.
    if (!product) return;

    if (!variant) {
      if (atc) atc.disabled = true;
      if (atcText) atcText.textContent = root.dataset.unavailableLabel || 'Unavailable';
      return;
    }

    var format = root.dataset.moneyFormat;
    var rolePrice = Number(root.dataset.rolePrice) || 0;

    if (variantInput) variantInput.value = String(variant.id);

    if (rolePrice > 0) {
      // Role pricing (app.role_prices) is a flat override for the whole
      // product, independent of the selected variant — keep showing it
      // instead of falling back to the variant's own price/compare-at.
      if (priceEl) priceEl.textContent = money(rolePrice, format);
      if (compareEl) {
        compareEl.hidden = false;
        compareEl.innerHTML = '<s>' + money(variant.price, format) + '</s>';
        compareEl.dataset.priceCents = String(variant.price);
      }
    } else {
      if (priceEl) priceEl.textContent = money(variant.price, format);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.hidden = false;
          compareEl.innerHTML = '<s>' + money(variant.compare_at_price, format) + '</s>';
          compareEl.dataset.priceCents = String(variant.compare_at_price);
        } else {
          compareEl.hidden = true;
          compareEl.innerHTML = '';
          delete compareEl.dataset.priceCents;
        }
      }
    }

    if (atc) atc.disabled = !variant.available;
    if (atcText) {
      atcText.textContent = variant.available
        ? root.dataset.addToCartLabel || 'Add to cart'
        : root.dataset.soldOutLabel || 'Sold out';
    }

    // Only rewrite the URL once the shopper actually picks something, so a plain
    // visit does not pick up a ?variant= query string.
    if (!isInitial && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }
  }

  function initGallery(root) {
    var mainEl = root.querySelector('[data-pdp-main]');
    var thumbsEl = root.querySelector('[data-pdp-thumbs]');
    if (!mainEl || typeof window.Swiper !== 'function') return;

    var thumbsSwiper = null;
    if (thumbsEl) {
      thumbsSwiper = new window.Swiper(thumbsEl, {
        direction: 'vertical',
        slidesPerView: 3,
        spaceBetween: 0,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        navigation: {
          prevEl: root.querySelector('[data-thumbs-prev]'),
          nextEl: root.querySelector('[data-thumbs-next]'),
        },
        breakpoints: {
          0: {
            direction: 'vertical',
            slidesPerView: 3,
          },
        },
      });
    }

    var mainSwiper = new window.Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
    });

    if (thumbsSwiper) {
      // Swiper's thumbs module already makes clicking a thumbnail move the
      // main image, and makes the main image's own navigation highlight the
      // matching thumbnail. It does NOT, however, make the thumb rail's own
      // prev/next arrows move the main image — those just page the thumbs
      // viewport. Drive the main swiper from the thumb rail's slideChange so
      // the arrows keep both in sync too (whichever thumb becomes active is
      // reflected as the main image).
      thumbsSwiper.on('slideChange', function () {
        mainSwiper.slideTo(thumbsSwiper.activeIndex);
      });
    }
  }

  function initOverview(root) {
    var toggleBtn = root.querySelector('[data-overview-toggle]');
    var longEl = root.querySelector('[data-overview-long]');
    var labelEl = toggleBtn ? toggleBtn.querySelector('[data-overview-toggle-label]') : null;
    if (!toggleBtn || !longEl || !labelEl) return;

    var labelMore = toggleBtn.dataset.labelMore || labelEl.textContent;
    var labelLess = toggleBtn.dataset.labelLess || labelMore;

    toggleBtn.addEventListener('click', function () {
      var expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      var next = !expanded;

      toggleBtn.setAttribute('aria-expanded', String(next));
      longEl.classList.toggle('is-expanded', next);
      labelEl.textContent = next ? labelLess : labelMore;
    });
  }

  // Volume pricing tiers are merchandising copy typed in by hand (no real
  // discount logic wired up on Shopify's side). Clicking a tier sets the
  // real quantity field and updates the displayed unit price, mirroring
  // what the legacy WP "Tiered Pricing Table" plugin's click handler does
  // client-side. IMPORTANT: this only changes what's shown on screen — it
  // does not make Shopify actually charge the discounted price at
  // checkout. Enforcing that for real needs a backend piece (a Shopify
  // Function/app reading structured tier data), which is out of scope here.
  function initTiers(root, signal) {
    var group = root.querySelector('[data-pdp-tiers]');
    if (!group) return;

    var tiers = Array.prototype.slice.call(group.querySelectorAll('[data-pdp-tier]'));
    if (tiers.length < 2) return;

    var qtyInput = root.querySelector('[data-qty-input]');
    var priceEl = root.querySelector('[data-price]');
    var compareEl = root.querySelector('[data-compare-price]');
    var format = root.dataset.moneyFormat;

    tiers.forEach(function (tier) {
      tier.addEventListener(
        'click',
        function () {
          tiers.forEach(function (other) {
            var active = other === tier;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-checked', active ? 'true' : 'false');
          });

          var quantity = Number(tier.dataset.pdpTierQuantity);
          if (qtyInput && quantity > 0) {
            qtyInput.value = String(quantity);
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          var priceAmount = Number(tier.dataset.pdpTierPriceAmount);
          if (priceEl && priceAmount > 0) {
            priceEl.textContent = money(Math.round(priceAmount * 100), format);
          }

          var compareAmount = Number(tier.dataset.pdpTierCompareAmount);
          if (compareEl) {
            if (compareAmount > 0 && priceAmount > 0 && compareAmount > priceAmount) {
              var compareCents = Math.round(compareAmount * 100);
              compareEl.hidden = false;
              compareEl.innerHTML = '<s>' + money(compareCents, format) + '</s>';
              compareEl.dataset.priceCents = String(compareCents);
            } else {
              compareEl.hidden = true;
              compareEl.innerHTML = '';
              delete compareEl.dataset.priceCents;
            }
          }
        },
        { signal: signal }
      );
    });
  }

  function initQuantity(root) {
    var input = root.querySelector('[data-qty-input]');
    var up = root.querySelector('[data-qty-up]');
    var down = root.querySelector('[data-qty-down]');
    if (!input) return;

    function setValue(next) {
      var min = Number(input.min || 1);
      var value = Math.max(min, Number(next || min));
      input.value = String(value);
    }

    if (up) {
      up.addEventListener('click', function () {
        setValue(Number(input.value || 1) + 1);
      });
    }

    if (down) {
      down.addEventListener('click', function () {
        setValue(Number(input.value || 1) - 1);
      });
    }
  }

  // The buy box form is plain server-rendered Liquid (no product-form-component
  // wrapper), so without this it just does a native POST to /cart/add and the
  // browser navigates away to /cart. This intercepts the submit, adds via the
  // cart AJAX endpoint instead, and dispatches the same CartLinesUpdateEvent
  // the rest of the theme listens for, so pdp-cart-modal and the header cart
  // count update exactly like the standard product-form-component flow does.
  function initAddToCart(root, signal) {
    var form = root.querySelector('[data-type="add-to-cart-form"]');
    var atc = root.querySelector('[data-atc]');
    var viewCartLink = root.querySelector('[data-view-cart-link]');
    var cartAddUrl = root.dataset.cartAddUrl;
    var cartUrl = root.dataset.cartUrl;
    if (!form || !cartAddUrl || !cartUrl) return;

    form.addEventListener(
      'submit',
      function (event) {
        event.preventDefault();

        if (atc && atc.disabled) return;
        if (atc) {
          atc.disabled = true;
          atc.classList.remove('is-added');
        }
        if (viewCartLink) viewCartLink.hidden = true;

        var formData = new FormData(form);
        var merchandiseId = formData.get('id');
        var quantity = Number(formData.get('quantity')) || 1;
        var deferredEventPromise = CartLinesUpdateEvent.createPromise();

        // Read the compare-at price straight off the buy box's own price
        // display (kept in sync by updateBuyBox/initTiers above), so the
        // add-to-cart modal can show what the shopper actually saw on screen.
        var compareEl = root.querySelector('[data-compare-price]');
        var compareAtPrice =
          compareEl && !compareEl.hidden && compareEl.dataset.priceCents
            ? Number(compareEl.dataset.priceCents)
            : null;

        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: merchandiseId, quantity: quantity }],
            promise: deferredEventPromise.promise,
          })
        );

        fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            if (response.status) {
              var error = new Error(response.message || 'Add to cart failed');
              deferredEventPromise.reject(error);
              throw error;
            }

            return fetch(cartUrl + '.js', {
              headers: { Accept: 'application/json' },
              credentials: 'same-origin',
            }).then(function (cartResponse) {
              return cartResponse.json();
            });
          })
          .then(function (ajaxCart) {
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                items: ajaxCart.items,
                source: 'amd-pdp',
                sourceId: root.dataset.sectionId,
                itemCount: quantity,
                didError: false,
                compareAtPrice: compareAtPrice,
              },
            });

            // Mirrors the WP/WooCommerce button: label gets a checkmark and a
            // "View cart" link appears underneath, both staying put (no
            // auto-revert) until the shopper adds again or reloads the page.
            if (atc) atc.classList.add('is-added');
            if (viewCartLink) viewCartLink.hidden = false;
          })
          .catch(function (error) {
            console.warn('[product-pdp] add to cart failed', error);
          })
          .finally(function () {
            if (atc) atc.disabled = false;
          });
      },
      { signal: signal }
    );
  }

  function initVariants(root, product) {
    var selects = root.querySelectorAll('[data-option-position]');
    if (!selects.length) return;

    function onChange(isInitial) {
      var selected = getSelectedOptions(root);
      var variant = findVariant(product, selected);
      updateBuyBox(root, product, variant, isInitial === true);
    }

    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        onChange(false);
      });
    });

    onChange(true);
  }

  function initTabs(root, signal) {
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-tab-trigger]'));
    var panels = root.querySelectorAll('[data-tab-panel]');
    if (!triggers.length || !panels.length) return;

    // Visibility is owned by CSS from here on, so that the tablet/mobile
    // breakpoint can stack every panel without fighting the hidden attribute.
    panels.forEach(function (panel) {
      panel.hidden = false;
    });

    function activate(trigger, moveFocus) {
      var key = trigger.getAttribute('data-tab-trigger');
      triggers.forEach(function (btn) {
        var active = btn === trigger;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.getAttribute('data-tab-panel') === key);
      });
      if (moveFocus) trigger.focus();
    }

    var initial = triggers.filter(function (t) {
      return t.classList.contains('is-active');
    })[0];
    triggers.forEach(function (t) {
      t.tabIndex = t === (initial || triggers[0]) ? 0 : -1;
    });

    triggers.forEach(function (trigger, index) {
      trigger.addEventListener(
        'click',
        function () {
          activate(trigger, false);
        },
        { signal: signal }
      );

      // Roving tabindex, per the ARIA authoring practices tabs pattern.
      trigger.addEventListener(
        'keydown',
        function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = triggers[(index + 1) % triggers.length];
          else if (event.key === 'ArrowLeft') next = triggers[(index - 1 + triggers.length) % triggers.length];
          else if (event.key === 'Home') next = triggers[0];
          else if (event.key === 'End') next = triggers[triggers.length - 1];
          if (!next) return;
          event.preventDefault();
          activate(next, true);
        },
        { signal: signal }
      );
    });
  }

  function initTooltips(root, signal) {
    var tooltips = root.querySelectorAll('[data-pdp-tooltip]');
    if (!tooltips.length) return;

    function closeAll(except) {
      tooltips.forEach(function (tooltip) {
        if (tooltip === except) return;
        tooltip.classList.remove('is-open');
        var btn = tooltip.querySelector('[data-pdp-tooltip-btn]');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    tooltips.forEach(function (tooltip) {
      var button = tooltip.querySelector('[data-pdp-tooltip-btn]');
      if (!button) return;

      button.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          closeAll(tooltip);
          var open = tooltip.classList.toggle('is-open');
          button.setAttribute('aria-expanded', String(open));
        },
        { signal: signal }
      );
    });

    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        // Close this section's tooltips unless the click landed on one of them.
        var inside = target instanceof Element ? target.closest('[data-pdp-tooltip]') : null;
        if (!inside || !root.contains(inside)) closeAll(null);
      },
      { signal: signal }
    );

    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape') closeAll(null);
      },
      { signal: signal }
    );
  }

  // Native <dialog> opens/closes instantly by default. WP's lightbox used
  // jQuery's fadeIn("slow")/fadeOut("slow"), so these helpers fade the dialog
  // (and its ::backdrop, via the .is-open class in CSS) in and out to match,
  // regardless of which trigger closes it (button, backdrop click or Escape).
  function openDialog(dialog) {
    dialog.showModal();
    window.requestAnimationFrame(function () {
      dialog.classList.add('is-open');
    });
  }

  function closeDialog(dialog) {
    if (!dialog.open) return;

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      dialog.close();
    }

    dialog.classList.remove('is-open');
    dialog.addEventListener('transitionend', finish, { once: true });
    // Fallback in case the transition never fires (e.g. reduced-motion).
    window.setTimeout(finish, 400);
  }

  // Esc triggers the dialog's native "cancel" event and would otherwise close
  // it instantly; intercept it so Escape fades out the same as every other trigger.
  function bindDialogFade(dialog, signal) {
    dialog.addEventListener(
      'cancel',
      function (event) {
        event.preventDefault();
        closeDialog(dialog);
      },
      { signal: signal }
    );
  }

  function initZoom(root, signal) {
    var dialog = root.querySelector('[data-pdp-zoom-dialog]');
    var image = root.querySelector('[data-pdp-zoom-image]');
    var closeBtn = root.querySelector('[data-pdp-zoom-close]');
    var triggers = root.querySelectorAll('[data-pdp-zoom]');
    if (!dialog || !image || !triggers.length || typeof dialog.showModal !== 'function') return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          var source = trigger.querySelector('img');
          image.src = trigger.getAttribute('href');
          image.alt = source ? source.alt : '';
          openDialog(dialog);
        },
        { signal: signal }
      );
    });

    if (closeBtn) {
      closeBtn.addEventListener(
        'click',
        function () {
          closeDialog(dialog);
        },
        { signal: signal }
      );
    }

    dialog.addEventListener(
      'click',
      function (event) {
        if (event.target === dialog) closeDialog(dialog);
      },
      { signal: signal }
    );

    bindDialogFade(dialog, signal);

    dialog.addEventListener(
      'close',
      function () {
        image.removeAttribute('src');
      },
      { signal: signal }
    );
  }

  // Matches the WP theme's YouTube ID regex (watch, embed, shortlink and
  // "vi=" style URLs) so the same range of URL formats keeps working.
  var YOUTUBE_ID_PATTERN =
    /(?:youtube(?:-nocookie)?\.com.*(?:v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/)|youtu\.be\/)([_0-9a-z-]+)/i;

  function getYoutubeId(url) {
    var match = typeof url === 'string' ? url.match(YOUTUBE_ID_PATTERN) : null;
    return match ? match[1] : null;
  }

  // Mirrors the WP theme's video lightbox: the thumbnail never plays inline,
  // clicking it always opens the video in a modal dialog.
  function initVideoModal(root, signal) {
    var dialog = root.querySelector('[data-pdp-video-dialog]');
    var frame = root.querySelector('[data-pdp-video-frame]');
    var closeBtn = root.querySelector('[data-pdp-video-close]');
    // Multiple triggers can share this one dialog: the main gallery's video
    // thumbnail plus one per Description-tab accordion item that has a
    // YouTube URL set.
    var triggers = root.querySelectorAll('[data-pdp-video-trigger]');
    if (!dialog || !frame || !triggers.length || typeof dialog.showModal !== 'function') return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener(
        'click',
        function () {
          var videoId = getYoutubeId(trigger.dataset.youtubeUrl);
          if (!videoId) return;
          frame.src = 'https://www.youtube.com/embed/' + videoId + '?rel=0&autoplay=1';
          openDialog(dialog);
        },
        { signal: signal }
      );
    });

    if (closeBtn) {
      closeBtn.addEventListener(
        'click',
        function () {
          closeDialog(dialog);
        },
        { signal: signal }
      );
    }

    dialog.addEventListener(
      'click',
      function (event) {
        if (event.target === dialog) closeDialog(dialog);
      },
      { signal: signal }
    );

    bindDialogFade(dialog, signal);

    dialog.addEventListener(
      'close',
      function () {
        // Drop the src so the embedded video actually stops playing.
        frame.src = '';
      },
      { signal: signal }
    );
  }

  // Below the tablet breakpoint the tab bar is hidden and every panel is
  // stacked, so the tab roles have to come off or the panels become orphaned
  // tabpanels with no owning tab.
  function initResponsiveTabRoles(root, signal) {
    var tablist = root.querySelector('.amd-product-pdp__tablist');
    var panels = root.querySelectorAll('[data-tab-panel]');
    if (!tablist || !panels.length || typeof window.matchMedia !== 'function') return;

    var query = window.matchMedia('(max-width: 1080px)');

    function apply() {
      var stacked = query.matches;
      tablist.setAttribute('role', stacked ? 'presentation' : 'tablist');
      panels.forEach(function (panel) {
        if (stacked) {
          panel.removeAttribute('role');
          panel.removeAttribute('tabindex');
          panel.removeAttribute('aria-labelledby');
        } else {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('tabindex', '0');
          var labelledBy = panel.dataset.labelledby;
          if (labelledBy) panel.setAttribute('aria-labelledby', labelledBy);
        }
      });
    }

    panels.forEach(function (panel) {
      var labelledBy = panel.getAttribute('aria-labelledby');
      if (labelledBy) panel.dataset.labelledby = labelledBy;
    });

    apply();
    query.addEventListener('change', apply, { signal: signal });
  }

  function initAccordion(root) {
    // Toggling `hidden` alone jumps straight from display:none to the full
    // panel height with no in-between frame, so the open/close always looked
    // instant no matter what CSS transition was set on the panel. Animating
    // an explicit pixel height (measured from scrollHeight) instead of
    // relying on `hidden`/display gives the browser something it can
    // actually interpolate.
    var prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var items = root.querySelectorAll('[data-pdp-accordion] .amd-product-pdp__acc-item');
    items.forEach(function (item) {
      var button = item.querySelector('.amd-product-pdp__acc-trigger');
      var panel = item.querySelector('.amd-product-pdp__acc-panel');
      if (!button || !panel) return;

      panel.style.height = '0px';

      panel.addEventListener('transitionend', function (event) {
        if (event.propertyName !== 'height') return;
        if (item.classList.contains('is-open')) {
          // Let the panel size itself naturally once fully open, so it keeps
          // adapting if its content changes height later (viewport resize,
          // an image finishing loading, etc.) without any extra JS.
          panel.style.height = 'auto';
        } else {
          panel.hidden = true;
        }
      });

      button.addEventListener('click', function () {
        var open = item.classList.contains('is-open');
        item.classList.toggle('is-open', !open);
        button.setAttribute('aria-expanded', String(!open));

        if (prefersReducedMotion) {
          panel.hidden = open;
          panel.style.height = open ? '0px' : 'auto';
          return;
        }

        if (open) {
          // Closing: pin the current rendered height first so the browser
          // has a concrete starting point to animate down from, instead of
          // jumping from "auto" straight to 0.
          panel.style.height = panel.scrollHeight + 'px';
          void panel.offsetHeight; // force reflow to commit the pinned height
          panel.style.height = '0px';
        } else {
          // Opening: un-hide first so scrollHeight reflects the real content
          // height, then animate up to it.
          panel.hidden = false;
          panel.style.height = panel.scrollHeight + 'px';
        }
      });
    });
  }

  function waitForSwiper(cb, attempts) {
    var left = typeof attempts === 'number' ? attempts : 40;
    if (typeof window.Swiper === 'function') {
      cb();
      return;
    }
    if (left <= 0) return;
    window.setTimeout(function () {
      waitForSwiper(cb, left - 1);
    }, 50);
  }

  var controllers = new WeakMap();

  function initSection(section) {
    if (!section || section.dataset.pdpReady === 'true') return;
    section.dataset.pdpReady = 'true';

    var controller = new AbortController();
    controllers.set(section, controller);

    var jsonEl = section.querySelector('[data-product-json]');
    var product = null;
    try {
      product = jsonEl ? JSON.parse(jsonEl.textContent || '{}') : null;
    } catch (e) {
      product = null;
    }

    waitForSwiper(function () {
      initGallery(section);
    });
    initOverview(section);
    initQuantity(section);
    initAddToCart(section, controller.signal);
    initTiers(section, controller.signal);
    initVariants(section, product);
    initTabs(section, controller.signal);
    initAccordion(section);
    initTooltips(section, controller.signal);
    initZoom(section, controller.signal);
    initVideoModal(section, controller.signal);
    initResponsiveTabRoles(section, controller.signal);
  }

  function destroySection(section) {
    var controller = controllers.get(section);
    if (controller) {
      controller.abort();
      controllers.delete(section);
    }
    if (section) delete section.dataset.pdpReady;
  }

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-product-pdp]').forEach(initSection);
  }

  // The script tag lives inside the section, so the theme editor can evaluate
  // this file more than once. Bind the lifecycle listeners a single time.
  if (!window.__amdPdpBound) {
    window.__amdPdpBound = true;

    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      event.target.querySelectorAll('[data-product-pdp]').forEach(destroySection);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
